<template>
  <div v-title="$route.meta.title"
       v-loading="loading"
       class="self-training-examination-result">
    <el-empty v-if="loadError"
              description="考试详情加载失败">
      <el-button type="primary" @click="loadResult">重新加载</el-button>
      <el-button @click="goBack">返回已完成考试</el-button>
    </el-empty>

    <template v-else-if="detail.record_id">
      <section class="result-summary">
        <div class="result-score">
          <span class="result-label">本次得分</span>
          <strong>{{ detail.score || 0 }}<small>/ {{ detail.full_score || 0 }}</small></strong>
        </div>
        <div class="result-stat">
          <span class="result-label">答对</span>
          <strong>{{ detail.correct_count || 0 }} / {{ detail.question_count || 0 }} 题</strong>
        </div>
        <div class="result-stat">
          <span class="result-label">正确率</span>
          <strong>{{ detail.correct_rate_text || '0%' }}</strong>
        </div>
        <div class="result-stat">
          <span class="result-label">用时</span>
          <strong>{{ detail.duration_text || '-' }}</strong>
        </div>
        <div class="result-submitted">
          <i class="el-icon-time"></i>
          提交时间：{{ detail.submitted_at || '-' }}
        </div>
        <div class="result-rate-ring">
          <span>正确率</span>
          <strong>{{ detail.correct_rate_text || '0%' }}</strong>
        </div>
      </section>

      <header class="result-heading">
        <div>
          <h2>{{ detail.title || '考试详情' }}</h2>
          <p>以下展示全部题目、你的作答与正确答案，请结合解析复习巩固</p>
        </div>
        <div class="result-legend">
          <span><i class="legend-dot is-correct"></i>回答正确</span>
          <span><i class="legend-dot is-wrong"></i>回答错误</span>
        </div>
      </header>

      <section v-for="(item, index) in detail.answer_list"
               :key="item.id || item.training_question_id || index"
               class="result-question">
        <div class="question-header">
          <div class="question-meta">
            <span class="question-index">第 {{ index + 1 }} 题</span>
            <span class="question-type">{{ item.question_type_name || '题目' }} · 分值 {{ item.max_score || 0 }} 分</span>
          </div>
          <span :class="['question-status', item.is_correct ? 'is-correct' : 'is-wrong']">
            <i :class="item.is_correct ? 'el-icon-check' : 'el-icon-close'"></i>
            {{ item.is_correct ? '回答正确' : '回答错误' }}
          </span>
        </div>

        <h3 class="question-title">{{ item.title }}</h3>
        <training-media-preview class="question-samples"
                                :files="item.sample_files" />

        <div v-if="(item.options || []).length" class="question-options">
          <div v-for="option in item.options"
               :key="option.uuid || option.key"
               :class="['question-option', getOptionClass(item, option)]">
            <span class="option-key">{{ option.key }}</span>
            <span class="option-title">{{ option.title }}</span>
            <span v-if="isUserAnswer(item, option)" class="option-tag">你的答案</span>
            <span v-if="isStandardAnswer(item, option)" class="option-tag is-standard">正确答案</span>
          </div>
        </div>

        <div class="answer-grid">
          <div class="answer-row">
            <span class="answer-label">你的答案</span>
            <div :class="['answer-box', item.is_correct ? 'is-correct' : 'is-wrong']">
              {{ item.user_answer_text || '未作答' }}
            </div>
          </div>
          <div class="answer-row">
            <span class="answer-label">正确答案</span>
            <div class="answer-box is-standard">{{ item.standard_answer_text || '-' }}</div>
          </div>
        </div>

        <div v-if="item.analysis_text || (item.analysis_files || []).length" class="analysis-box">
          <div class="analysis-title"><i class="el-icon-lightbulb"></i>答案解析</div>
          <p v-if="item.analysis_text">{{ item.analysis_text }}</p>
        </div>
      </section>

      <div v-if="!detail.answer_list || !detail.answer_list.length" class="result-empty">
        暂无答题记录
      </div>

      <div class="result-footer">
        <el-button type="primary" plain @click="goBack">返回已完成考试</el-button>
      </div>
    </template>
  </div>
</template>

<script>
import TrainingMediaPreview from '../../components/TrainingMediaPreview.vue'

export default {
  name: 'SelfTrainingExaminationResult',
  components: { TrainingMediaPreview },
  data () {
    return {
      loading: false,
      loadError: false,
      detail: {
        answer_list: [],
      },
    }
  },
  created () {
    this.loadResult()
  },
  methods: {
    loadResult () {
      this.loading = true
      this.loadError = false
      return request.common('getSelfTrainingExamResult', {
        record_id: this.$route.params.recordId,
        exam_id: this.$route.params.id,
      }).then(response => {
        this.detail = response && response.data ? response.data : { answer_list: [] }
      }).catch(() => {
        this.loadError = true
      }).finally(() => {
        this.loading = false
      })
    },
    goBack () {
      this.$router.push('/training/self-examination')
    },
    answerKeys (text) {
      if (!text || text === '-') return []
      return String(text).split('、').map(key => key.trim()).filter(Boolean)
    },
    isUserAnswer (item, option) {
      return this.answerKeys(item.user_answer_text).includes(option.key)
    },
    isStandardAnswer (item, option) {
      return this.answerKeys(item.standard_answer_text).includes(option.key)
    },
    getOptionClass (item, option) {
      const user = this.isUserAnswer(item, option)
      const standard = this.isStandardAnswer(item, option)
      if (user && standard) return 'is-user-correct'
      if (user && !standard) return 'is-user-wrong'
      if (standard) return 'is-standard'
      return ''
    },
  },
}
</script>

<style scoped lang="scss">
.self-training-examination-result {
  min-height: calc(100vh - 160px);
  padding: 26px 30px 40px;
  background: #f5f7fa;
  color: #1f2d3d;
}

.result-summary {
  position: relative;
  display: flex;
  align-items: center;
  gap: 38px;
  min-height: 150px;
  margin-bottom: 34px;
  padding: 30px 40px;
  border-radius: 16px;
  background: #2165ff;
  color: #fff;
  box-shadow: 0 8px 20px rgba(33, 101, 255, .16);
}

.result-score {
  min-width: 198px;
  padding-right: 38px;
  border-right: 1px solid rgba(255, 255, 255, .25);
  strong { display: block; margin-top: 8px; font-size: 54px; line-height: 1; }
  small { margin-left: 7px; font-size: 20px; font-weight: 400; }
}

.result-stat {
  min-width: 86px;
  strong { display: block; margin-top: 10px; font-size: 22px; }
}

.result-label { display: block; color: rgba(255, 255, 255, .78); font-size: 14px; }
.result-submitted { position: absolute; left: 356px; bottom: 25px; color: rgba(255, 255, 255, .82); font-size: 14px; }
.result-submitted i { margin-right: 6px; }

.result-rate-ring {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 112px;
  margin-left: auto;
  border: 5px solid rgba(255, 255, 255, .36);
  border-radius: 50%;
  span { color: rgba(255, 255, 255, .82); font-size: 13px; }
  strong { margin-top: 8px; font-size: 26px; }
}

.result-heading { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 18px; }
.result-heading h2 { margin: 0 0 8px; font-size: 23px; font-weight: 600; }
.result-heading p { margin: 0; color: #7b8798; font-size: 14px; }
.result-legend { display: flex; gap: 20px; color: #576579; font-size: 13px; }
.legend-dot { display: inline-block; width: 10px; height: 10px; margin-right: 6px; border-radius: 50%; }
.legend-dot.is-correct { background: #00b956; }
.legend-dot.is-wrong { background: #f0444e; }

.result-question {
  margin-bottom: 18px;
  padding: 30px 40px 34px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 6px rgba(31, 45, 61, .06);
}
.question-header { display: flex; align-items: center; justify-content: space-between; }
.question-meta { display: flex; align-items: center; gap: 18px; }
.question-index { padding: 8px 14px; border-radius: 8px; background: #f2f4f7; color: #38465a; font-size: 14px; font-weight: 600; }
.question-type { color: #9ba6b7; font-size: 14px; }
.question-status { padding: 8px 12px; border-radius: 6px; font-size: 13px; }
.question-status.is-correct { background: #e5f8ec; color: #00a94f; }
.question-status.is-wrong { background: #ffebed; color: #ed3945; }
.question-title { margin: 28px 0 24px; color: #1f2d3d; font-size: 19px; font-weight: 500; line-height: 1.6; }
.question-option { display: flex; align-items: center; min-height: 58px; margin: 12px 0; padding: 0 24px; border: 1px solid #e1e6ed; border-radius: 12px; color: #4a586b; }
.question-option.is-user-correct { border-color: #00b956; background: #f1fbf4; }
.question-option.is-user-wrong { border-color: #f0444e; background: #fff4f4; }
.question-option.is-standard { border-color: #80d6a3; background: #f6fcf8; }
.option-key { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; margin-right: 14px; border: 1px solid #dce2ea; border-radius: 6px; color: #788598; font-weight: 600; }
.is-user-correct .option-key, .is-standard .option-key { border-color: #00b956; background: #00b956; color: #fff; }
.is-user-wrong .option-key { border-color: #f0444e; background: #f0444e; color: #fff; }
.option-title { flex: 1; }
.option-tag { margin-left: 8px; padding: 5px 8px; border-radius: 5px; background: #c8f0d6; color: #00a94f; font-size: 12px; }
.option-tag.is-standard { background: #d7f3df; }
.answer-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 24px; }
.answer-label { display: block; margin-bottom: 8px; color: #6d7b8f; font-size: 13px; }
.answer-box { min-height: 44px; padding: 11px 14px; border-radius: 7px; background: #f6f7f9; color: #435166; line-height: 1.5; }
.answer-box.is-correct { border-left: 3px solid #00b956; background: #effaf3; color: #008e43; }
.answer-box.is-wrong { border-left: 3px solid #f0444e; background: #fff2f3; color: #d9333e; }
.answer-box.is-standard { border-left: 3px solid #2165ff; background: #eef4ff; color: #2165ff; }
.question-samples { margin-bottom: 24px; }
.analysis-box { margin-top: 22px; padding: 16px 20px; border-left: 4px solid #2165ff; border-radius: 0 10px 10px 0; background: #eef4ff; color: #46566d; }
.analysis-title { margin-bottom: 8px; color: #2165ff; font-size: 14px; }
.analysis-title i { margin-right: 7px; }
.analysis-box p { margin: 0; line-height: 1.7; }
.result-empty { padding: 60px; text-align: center; color: #909eb1; background: #fff; border-radius: 14px; }
.result-footer { margin-top: 24px; text-align: center; }

@media (max-width: 900px) {
  .result-summary { flex-wrap: wrap; gap: 20px; padding: 24px; }
  .result-score { min-width: 160px; padding-right: 20px; }
  .result-submitted { position: static; width: 100%; }
  .result-rate-ring { width: 92px; height: 92px; }
  .result-question { padding: 24px; }
}

@media (max-width: 600px) {
  .self-training-examination-result { padding: 20px 16px 30px; }
  .result-heading { display: block; }
  .result-legend { margin-top: 14px; }
  .question-header { align-items: flex-start; gap: 12px; }
  .question-meta { display: block; }
  .question-type { display: block; margin-top: 8px; }
  .answer-grid { grid-template-columns: 1fr; }
  .question-option { padding: 0 14px; }
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/exam/SelfTrainingExaminationResult.vue